import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

interface SpecialDate {
  date: string;
  type: 'closed' | 'special_hours';
  label?: string;
  hours?: {
    start: string;
    end: string;
  };
}

interface BusinessCalendarData {
  regularHolidays?: string[];
  specialDates?: SpecialDate[];
}

interface BusinessCalendarProps {
  businessCalendar: BusinessCalendarData;
  onChange: (calendar: BusinessCalendarData) => void;
}

const BusinessCalendar: React.FC<BusinessCalendarProps> = ({
  businessCalendar,
  onChange,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [newSpecialDate, setNewSpecialDate] = useState<SpecialDate>({
    date: '',
    type: 'closed',
    label: '',
  });

  const handleAddSpecialDate = () => {
    if (!newSpecialDate.date) return;

    const currentDates = businessCalendar.specialDates || [];

    if (editingIndex !== null) {
      // 編集モード
      const updatedDates = [...currentDates];
      updatedDates[editingIndex] = newSpecialDate;
      onChange({
        ...businessCalendar,
        specialDates: updatedDates,
      });
    } else {
      // 新規追加モード
      onChange({
        ...businessCalendar,
        specialDates: [...currentDates, newSpecialDate],
      });
    }

    handleCloseDialog();
  };

  const handleEditSpecialDate = (index: number) => {
    const date = businessCalendar.specialDates?.[index];
    if (date) {
      setNewSpecialDate(date);
      setEditingIndex(index);
      setOpenDialog(true);
    }
  };

  const handleDeleteSpecialDate = (index: number) => {
    const currentDates = businessCalendar.specialDates || [];
    onChange({
      ...businessCalendar,
      specialDates: currentDates.filter((_, i) => i !== index),
    });
  };

  const handleOpenDialog = () => {
    setNewSpecialDate({
      date: '',
      type: 'closed',
      label: '',
    });
    setEditingIndex(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingIndex(null);
    setNewSpecialDate({
      date: '',
      type: 'closed',
      label: '',
    });
  };

  const sortedSpecialDates = [...(businessCalendar.specialDates || [])].sort(
    (a, b) => a.date.localeCompare(b.date)
  );

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        営業カレンダー管理
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        臨時休業日や特別営業時間を設定できます。定休日は営業時間で設定してください。
      </Typography>

      {/* 特別営業日/臨時休業設定 */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            特別営業日・臨時休業
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            size="small"
          >
            追加
          </Button>
        </Box>

        {sortedSpecialDates.length === 0 ? (
          <Alert severity="info">
            特別営業日や臨時休業の設定がありません。「追加」ボタンから設定できます。
          </Alert>
        ) : (
          <Stack spacing={1}>
            {sortedSpecialDates.map((specialDate, index) => {
              const originalIndex = businessCalendar.specialDates?.indexOf(specialDate) ?? index;
              return (
                <Paper
                  key={index}
                  variant="outlined"
                  sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" fontWeight="bold">
                      {specialDate.date}
                      {specialDate.label && ` - ${specialDate.label}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {specialDate.type === 'closed' ? (
                        <Chip label="休業" color="error" size="small" sx={{ mr: 1 }} />
                      ) : (
                        <>
                          <Chip label="特別営業時間" color="primary" size="small" sx={{ mr: 1 }} />
                          {specialDate.hours && (
                            <span>
                              {specialDate.hours.start} 〜 {specialDate.hours.end}
                            </span>
                          )}
                        </>
                      )}
                    </Typography>
                  </Box>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handleEditSpecialDate(originalIndex)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteSpecialDate(originalIndex)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* 追加・編集ダイアログ */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingIndex !== null ? '特別営業日を編集' : '特別営業日を追加'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              type="date"
              label="日付"
              value={newSpecialDate.date}
              onChange={(e) => setNewSpecialDate({ ...newSpecialDate, date: e.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
            />

            <FormControl fullWidth>
              <InputLabel>種類</InputLabel>
              <Select
                value={newSpecialDate.type}
                label="種類"
                onChange={(e) =>
                  setNewSpecialDate({
                    ...newSpecialDate,
                    type: e.target.value as 'closed' | 'special_hours',
                  })
                }
              >
                <MenuItem value="closed">休業</MenuItem>
                <MenuItem value="special_hours">特別営業時間</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="ラベル（任意）"
              placeholder="例: 年末年始休業、臨時休業、夏季休暇"
              value={newSpecialDate.label || ''}
              onChange={(e) => setNewSpecialDate({ ...newSpecialDate, label: e.target.value })}
            />

            {newSpecialDate.type === 'special_hours' && (
              <>
                <TextField
                  fullWidth
                  type="time"
                  label="開店時間"
                  value={newSpecialDate.hours?.start || ''}
                  onChange={(e) =>
                    setNewSpecialDate({
                      ...newSpecialDate,
                      hours: {
                        start: e.target.value,
                        end: newSpecialDate.hours?.end || '',
                      },
                    })
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  fullWidth
                  type="time"
                  label="閉店時間"
                  value={newSpecialDate.hours?.end || ''}
                  onChange={(e) =>
                    setNewSpecialDate({
                      ...newSpecialDate,
                      hours: {
                        start: newSpecialDate.hours?.start || '',
                        end: e.target.value,
                      },
                    })
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button
            onClick={handleAddSpecialDate}
            variant="contained"
            disabled={!newSpecialDate.date}
          >
            {editingIndex !== null ? '更新' : '追加'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default BusinessCalendar;
